import { useCallback } from 'react';
import produce from 'immer';
import {
  SearchResultV2,
  SearchResponseV2,
  SearchResultAction as ISearchResultAction,
} from '@scale/shared/types/chat';
import ReferencesAccordion from './ReferencesAccordion';

type ReferencesSectionProps = {
  references: SearchResponseV2[];
  setReferences: (callback: (currentReferences: SearchResponseV2[]) => SearchResponseV2[]) => void;
  readOnly: boolean;
  handleAddSearchResultAction: (
    index: number,
    action: ISearchResultAction,
    referenceIndex: number,
  ) => void;
  handleEditSearchResultAction: (
    index: number,
    actionIndex: number,
    action: ISearchResultAction,
    referenceIndex: number,
  ) => void;
  handleRemoveSearchResultAction: (
    index: number,
    actionIndex: number,
    referenceIndex: number,
  ) => void;
  handleActionReorder: (
    index: number,
    sourceIndex: number,
    destinationIndex: number,
    referenceIndex: number,
  ) => void;
};

const ReferencesSection: React.FC<ReferencesSectionProps> = ({
  references,
  setReferences,
  readOnly,
  handleAddSearchResultAction,
  handleEditSearchResultAction,
  handleRemoveSearchResultAction,
  handleActionReorder,
}) => {
  const setReference = useCallback(
    (
      referenceIndex: number,
      update: {
        searchQuery: string;
        searchJustification: string;
        searchQueryResults: SearchResultV2[];
      },
    ) => {
      setReferences(prev =>
        produce(prev, draft => {
          if (update.searchQuery !== undefined) {
            draft[referenceIndex].searchTerm = update.searchQuery;
          }
          if (update.searchJustification !== undefined) {
            draft[referenceIndex].searchJustification = update.searchJustification;
          }
          if (update.searchQueryResults !== undefined) {
            draft[referenceIndex].searchResults = update.searchQueryResults;
          }
        }),
      );
    },
    [setReferences],
  );

  const deleteReference = useCallback(
    (referenceIndex: number) => {
      setReferences(prev => prev.filter((_, index) => index !== referenceIndex));
    },
    [setReferences],
  );

  if (!references.length) {
    return null;
  }

  return (
    <div>
      <div className="mb-1 text-sm font-medium text-neutral-700">References</div>
      <div className="flex flex-col gap-4">
        {references.map((reference, index) => (
          <div key={`${index}-${reference.searchTerm}`}>
            <ReferencesAccordion
              setReference={setReference}
              deleteReference={deleteReference}
              referenceIndex={index}
              existingSearchQuery={reference.searchTerm}
              existingSearchJustification={reference.searchJustification}
              existingSearchQueryResults={reference.searchResults}
              readOnly={readOnly}
              handleAddSearchResultAction={(...args) => handleAddSearchResultAction(...args, index)}
              handleEditSearchResultAction={(...args) =>
                handleEditSearchResultAction(...args, index)
              }
              handleRemoveSearchResultAction={(...args) =>
                handleRemoveSearchResultAction(...args, index)
              }
              handleActionReorder={(...args) => handleActionReorder(...args, index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReferencesSection;
